import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../../../../../generic-components/Layout';
import { DropArea } from '../../../../../components/form/DropArea/DropArea';
import { useUploadFileToOcr } from '../hooks/useUploadFileToOcr';
import Heading from '../../../../../generic-components/Heading';
import { ReactComponent as UploadBillIcon } from '../../../../../assets/billpay/upload-bill.svg';
import { IOcrData } from '../interfaces';
import { useStore } from '../../../../../store';
import { BASE_URL } from '../../../../../constants/endpoints';
import { MAX_FILE_SIZE_MB, NAVIGATION_ACTION_MAP } from '../consts';
import { VIEWS_MAP } from '../../../../../router/constants';
import { ACCEPTED_BILL_TYPES } from '../../../consts';
import Lottie from '../../../../../generic-components/Lottie';
import LoadingAnimation from '../../../../../assets/lotties/ocr_loading_lottie.json';
import { CloseButton, CloseButtonSize } from '../../../../../DesignSystem/LiliCloseButton/CloseButton';

export const UploadBill = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setBillData } = useStore((state) => state);
  const {
    cancelReq,
    mutation: { mutateAsync, isLoading, isSuccess },
  } = useUploadFileToOcr();
  const handleDropAreaFiles = (filesList: FileList) => {
    const list = Array.from(filesList);
    const fileToUpload = list[0];
    mutateAsync({ file: fileToUpload })
      .then((ocrData:IOcrData) => {
        setBillData({
          billId: '',
          amount: ocrData?.ocrResult?.amount || 0,
          billNumber: ocrData?.ocrResult?.billNumber || '',
          dueDate: ocrData?.ocrResult?.dueDate ? new Date(ocrData.ocrResult.dueDate) : new Date(),
          supplierName: ocrData.ocrResult.supplierName || '',
          billImageUrl: `${BASE_URL}${ocrData?.url}`,
          documentId: ocrData?.documentId,
          supplierId: ocrData?.ocrResult?.supplierId || '',
          assetType: fileToUpload?.type,
          assetName: fileToUpload?.name,
          paymentReminderData: null,
          recurringPaymentData: null,
        });
        navigate(NAVIGATION_ACTION_MAP.CREATE_BILL);
      });
  };

  return (
    <AppLayout
      layoutStyle='absolute inset-0 z-[50] bg-bw03 h-screen w-screen flex flex-col items-center justify-center fadeIn overflow-hidden'
      navBarStyle=''
      chatBotStyle='hidden'
      isWizardMode
    >
      {isLoading || isSuccess ? (
        <div className='flex flex-col items-center'>
          <Lottie lottieAsset={LoadingAnimation} isLoop isAutoplay className='w-[50px] mb-[24px]' />
          <Heading title={t('UPLOAD_BILL.LOADER_TITLE')} className='font-metricR text-[22px] text-bw08' />
          <Heading
            title={t('UPLOAD_BILL.LOADER_DISCLAIMER')}
            className='font-metricR text-[16px] text-bw07 mb-[24px]'
          />
          <button onClick={cancelReq} type='button' className='font-metricR text-[18px] text-bw08'>
            {t('UPLOAD_BILL.CANCEL_UPLOAD')}
          </button>
        </div>
      ) : (
        <div className='rounded-[12px] bg-bw00 flex flex-col'>
          <CloseButton onClick={() => navigate(`/${VIEWS_MAP.BILLS.BASE}/${VIEWS_MAP.BILLS.ADD_NEW_BILL}`)} className='absolute left-[34px] top-[17px]' id='closeAddBill' size={CloseButtonSize.Large} />
          <Heading title={t('UPLOAD_BILL.DROP_AREA_TITLE')} className='font-metricR text-[16px] text-bw08 py-[20px] px-[24px] border-b border-bw03' />
          <div className='w-[500px] h-[500px] py-[24px] px-[24px]'>
            <DropArea acceptedTypes={ACCEPTED_BILL_TYPES} maxFileSizeInMb={MAX_FILE_SIZE_MB} disclaimer={t('UPLOAD_BILL.DROP_AREA_DISCLAIMER')} IconAsProp={UploadBillIcon} onFilesChange={handleDropAreaFiles} />
          </div>
        </div>
      )}
    </AppLayout>
  );
};
